<template>
  <Teleport to="body">
    <Transition name="consent-fade">
      <div
        v-if="visible"
        class="call-bridge-consent-overlay"
        @click="handleCancel">
        <section
          class="call-bridge-consent-modal"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          @click.stop>
          <div class="call-bridge-consent-heading">
            <!-- 麦克风图标（Bridge / 通话类功能） -->
            <svg
              v-if="icon === 'mic'"
              class="call-bridge-consent-icon"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true">
              <rect x="9" y="2.5" width="10" height="15" rx="5" stroke="currentColor" stroke-width="2" />
              <path
                d="M5.5 13.5C5.5 18.1944 9.30558 22 14 22C18.6944 22 22.5 18.1944 22.5 13.5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round" />
              <path d="M14 22V26M10 26H18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path
                d="M11.5 7H16.5M11.5 11H16.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round" />
            </svg>
            <!-- 扬声器图标（Audio Share / 音频分享类功能） -->
            <svg
              v-else
              class="call-bridge-consent-icon"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true">
              <path d="M4 11V17H8L14 22V6L8 11H4Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
              <path
                d="M17.5 10.5C18.5 11.5 19 12.7 19 14C19 15.3 18.5 16.5 17.5 17.5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round" />
              <path
                d="M20.5 7.5C22.5 9.5 23.5 11.7 23.5 14C23.5 16.3 22.5 18.5 20.5 20.5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round" />
            </svg>
            <h2 :id="titleId">{{ title }}</h2>
          </div>

          <div class="call-bridge-consent-content">
            <p>{{ lead }}</p>
            <p v-for="(block, index) in blocks" :key="index">
              <strong>{{ block.strong }}</strong>&nbsp;&nbsp;{{ block.text }}
            </p>
          </div>

          <div class="call-bridge-consent-field" :class="{ 'has-error': showError }">
            <div class="call-bridge-consent-check-row">
              <input
                :id="checkboxId"
                v-model="agreed"
                class="call-bridge-consent-checkbox"
                type="checkbox" />
              <div class="call-bridge-consent-copy">
                <label :for="checkboxId">{{ ackPre }}</label>
                <a :href="termsHref" target="_blank" rel="noopener noreferrer">{{ termsText }}</a>
                <label :for="checkboxId">{{ ackPost }}</label>
              </div>
            </div>
            <p v-show="showError" class="call-bridge-consent-error" role="alert">
              {{ errorText }}
            </p>
          </div>

          <div class="call-bridge-consent-actions">
            <button
              class="call-bridge-consent-btn call-bridge-consent-cancel"
              type="button"
              @click="handleCancel">
              {{ cancelText }}
            </button>
            <button
              class="call-bridge-consent-btn call-bridge-consent-confirm"
              type="button"
              @click="handleConfirm">
              {{ confirmText }}
            </button>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue'

interface ConsentBlock {
  strong: string
  text: string
}

const props = withDefaults(
  defineProps<{
    visible: boolean
    title: string
    lead: string
    blocks: ConsentBlock[]
    ackPre: string
    termsText: string
    termsHref: string
    ackPost: string
    cancelText: string
    confirmText: string
    errorText: string
    icon?: 'mic' | 'speaker'
    idPrefix?: string
  }>(),
  {
    icon: 'speaker',
    idPrefix: 'privacyConsent',
  },
)

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

const agreed = ref(false)
const showError = ref(false)

const titleId = `${props.idPrefix}Title`
const checkboxId = `${props.idPrefix}Checkbox`

function setBodyLock(locked: boolean) {
  document.body.classList.toggle('call-bridge-consent-open', locked)
}

// Reset checkbox/error every time the dialog opens. Consent is never reused.
watch(
  () => props.visible,
  (val) => {
    setBodyLock(val)
    if (val) {
      agreed.value = false
      showError.value = false
    }
  },
)

onUnmounted(() => {
  setBodyLock(false)
})

function handleConfirm() {
  if (!agreed.value) {
    showError.value = true
    return
  }
  showError.value = false
  emit('confirm')
  emit('update:visible', false)
}

function handleCancel() {
  showError.value = false
  emit('cancel')
  emit('update:visible', false)
}
</script>

